import { useContext } from "react";

import { FormType, FulfillmentMethod, PaymentMethod } from "@simplyk/common";

import { useFrontendAuctionContext } from "../../../../../../common/contexts/FrontendAuctionContext";
import { FrontendFormContext } from "../../../../../../common/contexts/FrontendFormContext";
import { useFormV2Context } from "../../../contexts/FormV2Context";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";

type RenderPosition = "before-summary" | "after-payment" | null;

const PAYMENT_METHODS_PROVIDING_BUYER_INFORMATION = [
  PaymentMethod.ApplePayOrGooglePay,
  PaymentMethod.PayPal,
  PaymentMethod.Venmo,
];

export const paymentMethodCollectsBuyerBilling = (paymentMethod: PaymentMethod | undefined): boolean =>
  !paymentMethod || !PAYMENT_METHODS_PROVIDING_BUYER_INFORMATION.includes(paymentMethod);

const PAYMENT_METHODS_PROVIDING_SHIPPING_ADDRESS = [PaymentMethod.ApplePayOrGooglePay];

export const useBuyerInformationRenderPosition = (): RenderPosition => {
  const { selectedPaymentMethod, displayedFormAmount } = useContext(FrontendFormContext);
  const { hasAlreadyBid } = useFrontendAuctionContext();
  const { isPreview } = usePreviewContext();

  if (hasAlreadyBid) {
    return null;
  }

  const shouldShowBuyerInformation =
    isPreview ||
    displayedFormAmount === 0 ||
    (!!selectedPaymentMethod && !PAYMENT_METHODS_PROVIDING_BUYER_INFORMATION.includes(selectedPaymentMethod));

  return shouldShowBuyerInformation ? "after-payment" : null;
};

export const useIsBuyerBillingCollected = (): boolean => {
  const { selectedPaymentMethod } = useContext(FrontendFormContext);
  return paymentMethodCollectsBuyerBilling(selectedPaymentMethod);
};

export const useShippingAddressSectionShouldRender = (): boolean => {
  const { selectedPaymentMethod, displayedFormAmount } = useContext(FrontendFormContext);
  const { hasAlreadyBid } = useFrontendAuctionContext();
  const { isPreview } = usePreviewContext();

  const formV2Context = useFormV2Context();
  const commandFulfillment = formV2Context.type === FormType.Ticketing ? formV2Context.commandFulfillment : null;
  const fulfillmentMethod =
    formV2Context.type === FormType.Ticketing ? formV2Context.form.watch("fulfillmentMethod") : null;

  if (hasAlreadyBid) {
    return false;
  }

  const isShipping = fulfillmentMethod === FulfillmentMethod.Shipping || Boolean(commandFulfillment?.shippingOnly);
  if (!commandFulfillment?.showFulfillmentOnPayment || !isShipping) {
    return false;
  }

  if (isPreview) {
    return true;
  }

  if (displayedFormAmount === 0) {
    return true;
  }

  if (!selectedPaymentMethod) {
    return false;
  }
  return !PAYMENT_METHODS_PROVIDING_SHIPPING_ADDRESS.includes(selectedPaymentMethod);
};
